import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Hexagon, Fingerprint, Zap, Landmark, Brain, Shield, TrendingUp, Network } from 'lucide-react';

export const ExemplosMasterPage: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const portals = [
    { id: 'politico', title: 'Prefeito Conectado', subtitle: 'Poder & Política', icon: Landmark, color: 'from-amber-500 to-orange-900', link: '/exemplos/landing/politico', delay: 0.1 },
    { id: 'escola', title: 'Nervo Central', subtitle: 'Gestão Escolar & Equipe', icon: Network, color: 'from-blue-400 to-indigo-900', link: '/exemplos/landing/escola', delay: 0.2 },
    { id: 'professor', title: 'Mente Mestra', subtitle: 'Professores & Qualidade', icon: Brain, color: 'from-fuchsia-500 to-purple-900', link: '/exemplos/landing/professor', delay: 0.3 },
    { id: 'familia', title: 'Elo Seguro', subtitle: 'Família & Aluno', icon: Shield, color: 'from-emerald-400 to-teal-900', link: '/exemplos/landing/escola-publica', delay: 0.4 },
    { id: 'estudos', title: 'Matriz do Conhecimento', subtitle: 'Estudos & Tecnologia', icon: Zap, color: 'from-rose-500 to-pink-900', link: '/exemplos/landing/ensino-tecnico', delay: 0.5 },
    { id: 'dinheiro', title: 'Eficiência Pública', subtitle: 'Dinheiro & Economia', icon: TrendingUp, color: 'from-lime-400 to-green-900', link: '/exemplos/landing/economia', delay: 0.6 },
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative font-sans">
      
      {/* Psychedelic Abstract Background */}
      <div className="absolute inset-0 z-0 opacity-60 mix-blend-screen pointer-events-none fixed">
        <motion.div 
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-[-20%] left-[-10%] w-[70vw] h-[70vw] rounded-full bg-gradient-to-tr from-violet-700 via-fuchsia-600 to-transparent blur-[120px]"
        />
        <motion.div 
          animate={{ 
            x: mousePosition.x * -0.05,
            y: mousePosition.y * -0.05,
            rotate: [360, 0],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-gradient-to-tl from-cyan-500 via-blue-800 to-transparent blur-[140px]"
        />
        <motion.div 
          animate={{ 
            x: mousePosition.x * 0.08,
            y: mousePosition.y * 0.08,
            scale: [1, 1.4, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[30%] left-[30%] w-[50vw] h-[50vw] rounded-full bg-gradient-to-r from-amber-500 via-rose-600 to-transparent blur-[130px] opacity-50"
        />
      </div>

      {/* Grid Overlay */}
      <div className="absolute inset-0 z-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:4vw_4vw] pointer-events-none fixed"></div>

      <header className="relative z-10 px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 text-white/50 hover:text-white transition-colors uppercase tracking-widest text-xs font-bold z-50">
          <ArrowLeft size={16} /> Voltar ao Sistema Principal
        </Link>
        <div className="text-[10px] uppercase tracking-[0.4em] text-white/40 font-bold border border-white/10 px-4 py-2 rounded-full backdrop-blur-md hidden sm:block">
          Matriz Nexo // 2050
        </div>
      </header>

      <main className="relative z-10 flex flex-col items-center justify-center min-h-[85vh] px-4 py-12">
        
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="text-center mb-24 pointer-events-none"
        >
          <h1 className="text-5xl md:text-8xl font-black uppercase tracking-tighter mix-blend-overlay text-white leading-none">
            Ecossistema<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-fuchsia-500 to-cyan-400 mix-blend-normal drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]">
              Absoluto
            </span>
          </h1>
          <p className="mt-8 text-white/60 tracking-[0.3em] uppercase text-xs font-bold max-w-2xl mx-auto leading-loose">
            Política, Dinheiro, Tecnologia, Segurança e Qualidade de Vida.<br/>
            O Prefeito ligado a todos em 1 segundo. Selecione um vetor.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-10 w-full max-w-7xl justify-center items-center z-50">
          {portals.map((portal) => (
            <motion.div
              key={portal.id}
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: portal.delay, duration: 0.8, type: "spring", stiffness: 100 }}
              whileHover={{ scale: 1.02, y: -10 }}
              className="relative group w-full aspect-[4/3]"
            >
              <Link to={portal.link} className="block w-full h-full cursor-none" style={{ cursor: 'pointer' }}>
                {/* Hollow Glowing Border */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${portal.color} p-[2px] opacity-50 group-hover:opacity-100 transition-opacity duration-500 shadow-[0_0_40px_rgba(0,0,0,0.5)]`}>
                  <div className="absolute inset-0 bg-black/70 rounded-[22px] backdrop-blur-3xl"></div>
                </div>

                {/* Card Content */}
                <div className="relative h-full flex flex-col justify-between p-8 text-left z-10 overflow-hidden">
                  {/* Background Icon Watermark */}
                  <div className="absolute -bottom-10 -right-10 opacity-5 group-hover:opacity-10 transition-opacity duration-700 transform group-hover:scale-110 group-hover:rotate-12">
                    <portal.icon size={200} />
                  </div>

                  <div className="w-full flex justify-between items-start">
                    <motion.div 
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                      className="w-16 h-16 rounded-2xl border border-white/20 flex items-center justify-center backdrop-blur-md bg-white/5 group-hover:bg-white/20 transition-colors shadow-lg"
                    >
                      <portal.icon size={28} className="text-white/80 group-hover:text-white transition-colors" />
                    </motion.div>
                    <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                      <ArrowLeft size={14} className="text-white/30 group-hover:text-white rotate-135 transition-colors" style={{ transform: 'rotate(135deg)' }} />
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <p className="text-[10px] font-mono text-white/50 uppercase tracking-[0.3em] mb-2">{portal.subtitle}</p>
                    <h2 className="text-3xl font-black uppercase tracking-widest text-white mb-2 leading-none drop-shadow-md">{portal.title}</h2>
                  </div>
                  
                  <div className="w-full h-1 bg-white/10 mt-6 rounded overflow-hidden">
                    <motion.div 
                      className="h-full bg-white origin-left" 
                      initial={{ scaleX: 0 }} 
                      whileHover={{ scaleX: 1 }} 
                      transition={{ duration: 0.4, ease: "easeOut" }} 
                    />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

    </div>
  );
};
